import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import {
  Column,
  ColumnKeys,
  EasmScanConfigurationAssetGridWebComponentSchema,
  PREDEFINED_FILTERS_KEYS,
} from '../types';
import { WEB_COMPONENT_GRID_VIEW } from '../../../../webcomponents.constants';
import { GridViewWebComponent } from '../../../../grid/gridView';
import { EasmScanConfigurationAssetGridDataProvider } from '../dataProvider/EasmScanConfigurationAssetGridDataProvider';
import { actionsRenderer, dateRenderer } from './customGridRenderers';
import { EASMScanConfigurationEvents } from '../../easmScanConfiguration/types';
import { WebComponentEvent } from '../../../../framework/BaseWebComponentController';
import { translateFn } from '../../../../utils/translator.helper';

export class EasmScanConfigurationAssetGridView extends BaseWebComponentView {
  private companyId: string;

  public getDomElements(): void {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$gridView = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<Column, ColumnKeys>;
  }

  public buildView(data: EasmScanConfigurationAssetGridWebComponentSchema): void {
    if (!data) {
      this.dom.$wrapper.innerHTML = '';
      return;
    }

    this.buildGridView(data);
  }

  private buildGridView(data: EasmScanConfigurationAssetGridWebComponentSchema): void {
    const view = data.response.defaultViewProps;

    this.dom.$gridView.propertiesSet = false;
    this.dom.$gridView.dataProvider = new EasmScanConfigurationAssetGridDataProvider(this.dependencies, this.companyId);

    this.dom.$gridView.columnRenderers = {
      [ColumnKeys.DATE_ADDED]: dateRenderer.bind(this),
      [ColumnKeys.ACTIONS]: actionsRenderer.bind(this),
    };

    this.dom.$gridView.viewId = view.id;
    this.dom.$gridView.allColumnDetails = data.response.allColumns;
    this.dom.$gridView.columns = view.displayedColumns;
    this.dom.$gridView.hasFilters = true;
    this.dom.$gridView.filters = view.filters;
    this.dom.$gridView.predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
    this.dom.$gridView.filtersUiConfigs = this.getFiltersUiConfig();

    this.dom.$gridView.isCompactViewChecked = view.compactView;
    this.dom.$gridView.defaultViewProps = data.response.defaultViewProps;
    this.dom.$gridView.hasPagination = true;
    this.dom.$gridView.pageSize = view.pageSize;
    this.dom.$gridView.propertiesSet = true;

    this.dom.$gridView.customStrings = {
      noSearchResultsString: translateFn('No assets match your selected criteria.'),
    };

    if (this.dom.$gridView.injectDependencies) {
      this.dom.$gridView.injectDependencies(this.dependencies);
      this.dom.$gridView.start().then().catch(console.error);
    }
  }

  public onItemDelete(event: WebComponentEvent): void {
    const assetId = event.target.getAttribute('data-internal-id');

    this.dom.$gridView.dispatchEvent(
      new CustomEvent(EASMScanConfigurationEvents.REMOVE_ASSET_FROM_SCAN, {
        detail: {
          assetId: assetId,
          data: this.dom.$gridView.dataProvider.getData(),
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  public setCompanyId(companyId: string): void {
    this.companyId = companyId;
  }

  private getFiltersUiConfig(): Array<{ key: ColumnKeys; config: any }> {
    return [
      {
        key: ColumnKeys.DATE_ADDED,
        config: {
          additionalTimeRangeOptions: [
            {
              label: translateFn('Last 90 days'),
              value: 90,
            },
            {
              label: translateFn('Last 180 days'),
              value: 180,
            },
            {
              label: translateFn('Last 365 days'),
              value: 365,
            },
          ],
          enableSelectAllToDate: true,
        },
      },
    ];
  }
}
